import 'babel-polyfill';
import Vue from 'vue';
import BootstrapVue from 'bootstrap-vue';
import VeeValidate from 'vee-validate';
import VueClipboard from 'vue-clipboard2';
import Vuex from 'vuex';
import * as VueGoogleMaps from 'vue2-google-maps';

// Reusuable Generic components
import AppIcon from '@/components/app-icon.vue';
import FitText from '@/components/fit-text.vue';
import NavDropdown from '@/components/aha-nav-dropdown';
import SelectDropdown from '@/components/aha-select-dropdown';
import SelectDropdownItem from '@/components/aha-select-dropdown-item';
import LocationSelector from '@/components/location-selector.vue';
import LocationSelectorForm from '@/components/location-selector-form.vue';
import LocationSelectorTable from '@/components/location-selector-table.vue';
import StrokeGroupSelector from '@/components/stroke-group-selector.vue';
import StrokeGroupSelectorForm from '@/components/stroke-group-selector-form.vue';
import StrokeGroupSelectorTable from '@/components/stroke-group-selector-table.vue';
import YouTube from '@/components/aha-youtube.js';
import Notification from '@/components/aha-notification.vue';
import MobileNav from '@/components/aha-mobile-nav.vue';
import MobileNavPanel from '@/components/aha-mobile-nav-panel.vue';
import mobileNavSearchPanel from '@/components/aha-mobile-nav-search-panel';
import RecipeCheckbox from '@/components/aha-recipe-checkbox.js';
import RecipeSocial from '@/components/aha-recipe-social.js';
import Modal from '@/components/aha-modal.js';
import ListBox from '@/components/aha-listbox.js';
import AHAGoogleMap from '@/components/aha-google-map';
import HalfImageHalfCopyCollapse from '@/components/aha-half-image-half-copy-collapse.vue';
import DonationModal from '@/components/aha-donation-modal.vue';
import HospitalZipCodeModal from '@/components/aha-hospital-zipcode-modal.vue';
import SessionTimeOutModal from '@/components/aha-session-timeout-modal.vue';
import TransitionGroup from '@/components/aha-transition-group';
import TransitionGroupForm from '@/components/aha-transition-group-form';
import TransitionStep from '@/components/aha-transition-step';
import TransitionTrigger from '@/components/aha-transition-trigger';
import TransitionInputValue from '@/components/aha-transition-input-value';
import InlineSiteSearch from '@/components/aha-inline-site-search.vue';
import ImpactedData from '@/components/impacted-data.vue';
import SearchResultsForm from '@/components/aha-search-results-form';
import BackToSearchLink from '@/components/aha-back-to-search.vue';
import Share from '@/components/aha-share.js';
import ShareGigya from '@/components/aha-share-gigya.js';
import SingleFieldPromptForm from '@/components/aha-single-field-prompt-form.vue';
import SingleFieldPromptRadioForm from '@/components/aha-single-field-prompt-radio-form';
import TvPageModal from '@/components/aha-tv-page-modal';
import MakeCommitmentForm from '@/components/aha-make-commitment-form';
import AddToRecipeBox from '@/components/aha-add-to-recipe-box.vue';
import RecipeBoxFavorite from '@/components/aha-recipe-box-favorite.vue';
import ModifyRecipeBox from '@/components/aha-modify-recipe-box.vue';
import CopyUrlToClipboard from '@/components/aha-copy-url-to-clipboard.vue';
import ExtScript from '@/components/aha-external-script.vue';
import SupportBarSection from '@/components/footer-support-bar-section.vue';
import SimpleAccordion from '@/components/simple-accordion.vue';
import AHADafWidget from '@/components/aha-daf-widget.vue';
import CollapsableView from '@/components/aha-collapsable-view.vue';
import SelectionCard from '@/components/aha-selection-card';
import SwitchInput from '@/components/aha-switch-input';
import CardSelector from '@/components/aha-card-selector';
import CardSelectorFilter from '@/components/aha-card-selector-filter';
import CardViewer from '@/components/aha-card-viewer';
import CardViewerText from '@/components/aha-card-viewer-text';
import MaskedInput from '@/components/aha-masked-input';
import CPRCourseSearchWrapper from '@/components/aha-cpr-search-course-wrapper';
import ScienceVolunteerModal from '@/components/phd/phd-science-volunteer-modal.vue';
import NewsletterAjaxThankYou from '@/components/aha-newsletter-ajax-thank-you.vue';
import SitecoreFormWrapper from '@/components/aha-sitecore-form-wrapper.vue';
import RatingsWidgetStar from '@/components/ratings-widget-star.vue';
import ImageVideoCarousel from '@/components/aha-carousel.vue';
import GroceryList from '@/components/aha-grocery-list.vue';
import GroceryCount from '@/components/aha-grocery-count.vue';
// added contestant grid
import ContestantGrid from '@/components/aha-contestant-grid.vue';

// Global Filter
import PluralizeFilter from '@/filters/pluralize.js';

// Global directives
import verticalAlign from '@/directives/vertical-align';
import lazyImg from '@/directives/lazy-img';
import toggleChecked from '@/directives/toggle-checked';
import toggleCreditImage from '@/directives/toggle-credit-image';
import perfectScrollbar from '@/directives/perfect-scrollbar';
import shareButton from '@/directives/share-button';
import speedBumpDirective from '@/directives/speed-bump';
import ssoInterceptDirective from '@/directives/sso-intercept';
import printElement from '@/directives/print-element';
import registerEvent from '@/directives/register-event';
import formSubmit from '@/directives/form-submit';
import formValidate from '@/directives/form-validate';
import formSubmitCheckbox from '@/directives/form-submit-checkbox';
import buttonCollapseContent from '@/directives/button-collapse-content';
import imageVideoCarousel from '@/directives/image-video-carousel';

import luminateDonateForm from '@/directives/luminate-donate-form';
import maxChildrenHeight from '@/directives/max-children-height';
import nestedAccordion from '@/directives/nested-accordion';
import tabs from '@/directives/tabs';
import tabsCustomTabbing from '@/directives/tabs-custom-tabbing';
import hiddenInput from '@/directives/hidden-input';
import input from '@/directives/input';
import youtubeModal from '@/directives/youtube-modal';
import closeCoveoModal from '@/directives/close-coveo-modal';
import formSubmitGroceryEmail from '@/directives/form-submit-grocery-email';
import votingAction from '@/directives/voting-action';
import votingMarketingOptedIn from '@/directives/voting-marketing-optedIn';

import setSpeedBumpClassLinks from '@/utils/speed-bump-links';
// import setSsoInterceptLinks from '@/utils/sso-intercept-links'; // removed by C.Castle
import skipToLink from '@/utils/skip-to';
import selectAll from '@/utils/select-all';
// import setMainNavActive from '@/utils/main-nav-active';
import speedBumpTabbing from '@/utils/speed-bump-modal-tabbing';
import customEventPolyfill from '@/utils/custom-event';
import objectFitPolyfill from '@/utils/object-fit';
import localStoragePolyfill from '@/utils/localstorage';
import fromStore from '@/directives/from-store';
import inputMaskDirective from '@/directives/input-mask';
import stickyHeader from '@/directives/sticky-header';
import analyticsCampaignTracker from '@/utils/analytics-campaign-tracker';
import courseSearchForm from '@/directives/course-search-form';
import proVolunteerSearch from '@/directives/phd-pro-vol-search';
// Plugins
import VJSDropdown from '@/plugins/vjs-dropdown/authoring';
import vjsDropdownCoveo from '@/utils/vjs-dropdown-coveo';
import coveoEvents from '@/utils/coveo-events';
import FRU from '@/utils/fundraise-up';
import iOS from '@/utils/detect-ios';
import revealElementBehindSticky from '@/utils/reveal-element-behind-sticky';
import { Cookie } from '@/utils/aha-cookie';
import { itemsStorageFilters, itemsStorageFunc } from './utils/items-storage';
import ckmRiskCalculator from '@/components/phd/ckm-risk-calculator';
import HCFCalculator from '@/components/phd/hcf-calculator';
import AhaSearchDropdown from '@/components/phd/aha-search-dropdown';
import PrimacyCalculator from '../../demo/pages/components/primacy-calculator.vue';
import AhaSaveVideo from '../../demo/pages/components/aha-save-video.vue';
import AhaSavedVideos from '../../demo/pages/components/aha-saved-videos.vue';
import AhaSaveTeaserVideo from '../../demo/pages/components/aha-save-teaser-video.vue';
import AhaResetPersonalization from '../../demo/pages/components/aha-reset-personalization.vue';
import ahaSearchFundingOpportunities from '@/components/phd/aha-search-funding-opportunities.vue';
// Phd Components
import '@/phd';
import '@/ecc';

// HCxF Components
import '@/hcxf';

// Wbwb Components
import '@/wbwb';

// Expose VanillaJS Dropdown plugin to Global scope
window.VJSDropdown = new VJSDropdown();

Vue.use(BootstrapVue);
Vue.use(VeeValidate);
Vue.use(VueClipboard);
Vue.use(Vuex);
Vue.use(VueGoogleMaps, {
    load: {
        key: 'AIzaSyAfoI8CjSCPhRd7IYXmgAbArVRkEdGvMyU',
        libraries: 'places'
    }
});

Vue.filter('pluralize', PluralizeFilter);

Vue.directive('vertical-align', verticalAlign);
Vue.directive('lazy-img', lazyImg);
Vue.directive('toggle-checked', toggleChecked);
Vue.directive('toggle-credit-image', toggleCreditImage);
Vue.directive('share-button', shareButton);
Vue.directive('perfect-scrollbar', perfectScrollbar);
Vue.directive('speed-bump', speedBumpDirective);
Vue.directive('sso-intercept', ssoInterceptDirective);
Vue.directive('print-element', printElement);
Vue.directive('register-event', registerEvent);
Vue.directive('form-submit', formSubmit);
Vue.directive('form-validate', formValidate);
Vue.directive('form-submit-checkbox', formSubmitCheckbox);
Vue.directive('button-collapse-content', buttonCollapseContent);
Vue.directive('image-video-carousel', imageVideoCarousel);
Vue.directive('luminate-donate-form', luminateDonateForm);
Vue.directive('max-children-height', maxChildrenHeight);
Vue.directive('nestedAccordion', nestedAccordion);
Vue.directive('tabs', tabs);
Vue.directive('tabs-custom-tabbing', tabsCustomTabbing);
Vue.directive('hidden-input', hiddenInput);
Vue.directive('input', input);
Vue.directive('youtube-modal', youtubeModal);
Vue.directive('close-coveo-modal', closeCoveoModal);
Vue.directive('from-store', fromStore);
Vue.directive('aha-mask', inputMaskDirective);
Vue.directive('sticky-header', stickyHeader);
Vue.directive('course-search-form', courseSearchForm);
Vue.directive('phd-pro-vol-search', proVolunteerSearch);
Vue.directive('form-submit-grocery-email', formSubmitGroceryEmail);
Vue.directive('ckm-risk-calculator', ckmRiskCalculator);
Vue.directive('hcf-calculator', HCFCalculator);
Vue.directive('voting-action', votingAction);
Vue.directive('voting-marketing-optedin', votingMarketingOptedIn);
Vue.directive('aha-search-dropdown', AhaSearchDropdown);
Vue.directive('aha-search-funding-opportunities', ahaSearchFundingOpportunities);

// Non-visual required components
Vue.component('app-icon', AppIcon);
Vue.component('fit-text', FitText);
Vue.component('aha-nav-dropdown', NavDropdown);
Vue.component('aha-select-dropdown', SelectDropdown);
Vue.component('aha-select-dropdown-item', SelectDropdownItem);
Vue.component('aha-location-selector', LocationSelector);
Vue.component('aha-location-selector-form', LocationSelectorForm);
Vue.component('aha-location-selector-table', LocationSelectorTable);
Vue.component('aha-stroke-group-selector', StrokeGroupSelector);
Vue.component('aha-stroke-group-selector-form', StrokeGroupSelectorForm);
Vue.component('aha-stroke-group-selector-table', StrokeGroupSelectorTable);
Vue.component('aha-youtube', YouTube);
Vue.component('aha-notification', Notification);
Vue.component('aha-mobile-nav', MobileNav);
Vue.component('aha-mobile-nav-panel', MobileNavPanel);
Vue.component('aha-mobile-nav-search-panel', mobileNavSearchPanel);
Vue.component('aha-recipe-checkbox', RecipeCheckbox);
Vue.component('aha-recipe-social', RecipeSocial);
Vue.component('aha-modal', Modal);
Vue.component('aha-listbox', ListBox);
Vue.component('aha-donation-modal', DonationModal);
Vue.component('aha-hospital-zipcode-modal', HospitalZipCodeModal);
Vue.component('aha-session-timeout-modal', SessionTimeOutModal);
Vue.component('aha-inline-site-search', InlineSiteSearch);
Vue.component('impacted-data', ImpactedData);
Vue.component('aha-back-to-search-link', BackToSearchLink);
Vue.component('aha-share', Share);
Vue.component('aha-share-gigya', ShareGigya);
Vue.component('aha-transition-group', TransitionGroup);
Vue.component('aha-transition-group-form', TransitionGroupForm);
Vue.component('aha-transition-step', TransitionStep);
Vue.component('aha-transition-trigger', TransitionTrigger);
Vue.component('aha-transition-input-value', TransitionInputValue);
Vue.component('aha-search-results-form', SearchResultsForm);
Vue.component('aha-single-field-prompt-form', SingleFieldPromptForm);
Vue.component('aha-single-field-prompt-radio-form', SingleFieldPromptRadioForm);
Vue.component('aha-tv-page-modal', TvPageModal);
Vue.component('aha-make-commitment-form', MakeCommitmentForm);
Vue.component('aha-add-to-recipe-box', AddToRecipeBox);
Vue.component('aha-recipe-box-favorite', RecipeBoxFavorite);
Vue.component('aha-modify-recipe-box', ModifyRecipeBox);
Vue.component('aha-google-map', AHAGoogleMap);
Vue.component('aha-half-image-half-copy-collapse', HalfImageHalfCopyCollapse);
Vue.component('aha-copy-url-to-clipboard', CopyUrlToClipboard);
Vue.component('aha-external-script', ExtScript);
Vue.component('support-bar-section', SupportBarSection);
Vue.component('simple-accordion', SimpleAccordion);
Vue.component('aha-daf-widget', AHADafWidget);
Vue.component('aha-selection-card', SelectionCard);
Vue.component('aha-card-selector', CardSelector);
Vue.component('aha-card-selector-filter', CardSelectorFilter);
Vue.component('aha-card-viewer', CardViewer);
Vue.component('aha-card-viewer-text', CardViewerText);
Vue.component('aha-switch-input', SwitchInput);
Vue.component('aha-collapsable-view', CollapsableView);
Vue.component('aha-masked-input', MaskedInput);
Vue.component('aha-cpr-search-course-wrapper', CPRCourseSearchWrapper);
Vue.component('phd-science-volunteer-modal', ScienceVolunteerModal);
Vue.component('aha-newsletter-ajax-thank-you', NewsletterAjaxThankYou);
Vue.component('aha-sitecore-form-wrapper', SitecoreFormWrapper);
Vue.component('ratings-widget-star', RatingsWidgetStar);
Vue.component('aha-carousel', ImageVideoCarousel);
Vue.component('aha-grocery-list', GroceryList);
Vue.component('aha-grocery-count', GroceryCount);
Vue.component('ckm-risk-calculator', ckmRiskCalculator);
Vue.component('hcf-calculator', HCFCalculator);
// added new aha contestant grid
Vue.component('aha-contestant-grid', ContestantGrid);
Vue.component('primacy-calculator', PrimacyCalculator);
Vue.component('aha-reset-personalization', AhaResetPersonalization);
Vue.component('aha-save-video', AhaSaveVideo);
Vue.component('aha-saved-videos', AhaSavedVideos);
Vue.component('aha-save-teaser-video', AhaSaveTeaserVideo);
Vue.directive('aha-search-funding-opportunities', ahaSearchFundingOpportunities);

Vue.config.ignoredElements = ['atomic-search-interface', 'atomic-search-layout', 'atomic-layout-section', 'atomic-search-box', 'atomic-facet-manager', 'atomic-category-facet', 'atomic-facet', 'atomic-numeric-facet', 'atomic-numeric-range', 'atomic-timeframe-facet', 'atomic-timeframe', 'atomic-rating-facet', 'atomic-rating-range-facet', 'atomic-color-facet', 'atomic-format-currency', 'atomic-segmented-facet-scrollable', 'atomic-segmented-facet', 'atomic-popover', 'atomic-breadbox', 'atomic-query-summary', 'atomic-refine-toggle', 'atomic-sort-dropdown', 'atomic-sort-expression', 'atomic-did-you-mean', 'atomic-notifications', 'atomic-smart-snippet', 'atomic-smart-snippet-suggestions', 'atomic-query-error', 'atomic-no-results', 'atomic-load-more-results', 'atomic-result-list', 'atomic-result-template', 'atomic-result-section-visual', 'atomic-result-section-title', 'atomic-result-link', 'atomic-result-section-excerpt', 'atomic-result-text'];

Vue.prototype.$eventHub = new Vue();

/* eslint-disable */
window.ahavm = new Vue({
    el: '#app'
});

// Set up the speed-bump hack to bridge between vanilla JS and Vue
setSpeedBumpClassLinks('.twoClick, .twoclick', '#speed-bump-link');
// setSsoInterceptLinks('#sso-intercept-link'); // removed by C.Castle
skipToLink('.c-skip-to');
selectAll();
// setMainNavActive();
speedBumpTabbing();
vjsDropdownCoveo();
coveoEvents();
customEventPolyfill();
objectFitPolyfill();
localStoragePolyfill();
analyticsCampaignTracker();
FRU();
iOS();
revealElementBehindSticky();
window.AHACookieAPI = Cookie;
window.AHAItemsStorageAPI = { itemsStorage: itemsStorageFunc, filters: itemsStorageFilters };



// WEBPACK FOOTER //
// ./src/app/index.js